(function() {
  'use strict';

  // Simplified state management
  const state = {
    selectedQuantity: 10,
    logoFile: null,
    decorationType: '', // embroidery, leather, fabric
    decorationSize: '',
    decorationMaterial: '', // material handle (only for leather/fabric)
    decorationMaterialTitle: '', // material display title
    decorationLocation: '',
    beanieSelections: [],
    rushProduction: false,
    rushProductInfo: null, // { variantId, variantPrice, variantTitle }
    isConfigComplete: false
  };

  // Flag to prevent infinite loop when auto-updating quantity
  let isAutoUpdatingQuantity = false;

  // Initialize
  function init() {
    setupQuantitySelector();
    setupQuickSelectButtons();
    setupDecorationSelection();
    setupBeanieSelection();
    setupPowerUp();
    initLogoManager();
    setupOrderButtons();
    setupNotificationSystem();
    updatePricing();
    updateStatusIndicators();
  }

  // ============================================
  // NOTIFICATION SYSTEM
  // ============================================
  function setupNotificationSystem() {
    // Create notification container if it doesn't exist
    if (!document.getElementById('notification-container')) {
      const container = document.createElement('div');
      container.id = 'notification-container';
      container.className = 'fixed top-4 right-4 z-[200] space-y-2';
      document.body.appendChild(container);
    }
  }

  function showNotification(message, type = 'error') {
    const container = document.getElementById('notification-container');
    if (!container) return;

    const notification = document.createElement('div');
    const bgColor = type === 'error' ? 'bg-red-500' : type === 'success' ? 'bg-green-500' : 'bg-blue-500';

    notification.className = `${bgColor} text-white px-6 py-4 rounded-lg shadow-lg flex items-center space-x-3 animate-slide-in max-w-md`;
    notification.innerHTML = `
      <svg class="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        ${type === 'error'
          ? '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>'
          : '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>'}
      </svg>
      <span class="flex-1">${message}</span>
      <button class="text-white hover:text-gray-200 transition-colors" onclick="this.parentElement.remove()">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
        </svg>
      </button>
    `;

    container.appendChild(notification);

    // Auto-remove after 5 seconds
    setTimeout(() => {
      notification.style.opacity = '0';
      notification.style.transform = 'translateX(100%)';
      setTimeout(() => notification.remove(), 300);
    }, 5000);
  }

  // ============================================
  // STATUS INDICATORS
  // ============================================
  function updateStatusIndicators() {
    // Check logo status
    const hasLogo = logoManager?.hasLogo();
    updateStatusIndicator('logo', hasLogo);

    // Check decoration status
    // For embroidery, shape is not required
    let hasDecoration = state.decorationType && state.decorationLocation;

    // For non-embroidery types, shape is required
    if (state.decorationType !== 'embroidery') {
      hasDecoration = hasDecoration && state.decorationSize;
    }

    // For leather and fabric, also check material selection
    if (hasDecoration && (state.decorationType === 'leather' || state.decorationType === 'fabric')) {
      hasDecoration = hasDecoration && state.decorationMaterial;
    }

    updateStatusIndicator('decoration', hasDecoration);

    // Check beanies status
    const hasBeanies = state.beanieSelections.length > 0;
    updateStatusIndicator('beanies', hasBeanies);
  }

  function updateStatusIndicator(type, isComplete) {
    const indicator = document.querySelector(`[data-status-indicator="${type}"]`);
    const label = document.querySelector(`[data-status-label="${type}"]`);

    if (!indicator || !label) return;

    if (isComplete) {
      indicator.classList.remove('bg-gray-300', 'text-gray-600');
      indicator.classList.add('bg-green-500', 'text-white');
      indicator.innerHTML = '✓';
      label.classList.remove('text-gray-600');
      label.classList.add('text-green-600', 'font-medium');
    } else {
      indicator.classList.remove('bg-green-500', 'text-white');
      indicator.classList.add('bg-gray-300', 'text-gray-600');
      const number = type === 'logo' ? '1' : type === 'decoration' ? '2' : '3';
      indicator.textContent = number;
      label.classList.remove('text-green-600', 'font-medium');
      label.classList.add('text-gray-600');
    }
  }

  // ============================================
  // BEANIE COUNTER
  // ============================================
  function updateBeanieCounter() {
    const countElement = document.querySelector('[data-beanie-count]');
    const neededElement = document.querySelector('[data-beanie-needed]');
    const counterContainer = document.querySelector('[data-beanie-counter]');

    if (!countElement || !neededElement || !counterContainer) return;

    // Calculate total selected beanies
    const totalSelected = state.beanieSelections.reduce((sum, beanie) => sum + beanie.quantity, 0);
    const targetQuantity = state.selectedQuantity;

    // Update display
    countElement.textContent = totalSelected;
    neededElement.textContent = targetQuantity;

    // Visual feedback based on selection status
    counterContainer.classList.remove('border-blue-300', 'border-green-500', 'border-orange-500', 'border-red-500', 'bg-white', 'bg-green-50', 'bg-orange-50', 'bg-red-50');

    // Reset text colors
    const textElements = [countElement, neededElement];
    textElements.forEach(el => {
      el.classList.remove('text-blue-900', 'text-green-900', 'text-orange-900', 'text-red-900');
    });

    if (totalSelected === targetQuantity && totalSelected > 0) {
      // Perfect match - green
      counterContainer.classList.add('border-green-500', 'bg-green-50');
      textElements.forEach(el => el.classList.add('text-green-900'));

      // Remove excess indicator if exists
      const excessIndicator = counterContainer.querySelector('[data-excess-indicator]');
      if (excessIndicator) excessIndicator.remove();

    } else if (totalSelected > targetQuantity) {
      // Over target - orange with +X indicator
      const excess = totalSelected - targetQuantity;
      counterContainer.classList.add('border-orange-500', 'bg-orange-50');
      textElements.forEach(el => el.classList.add('text-orange-900'));

      // Show +X indicator
      let excessIndicator = counterContainer.querySelector('[data-excess-indicator]');
      if (!excessIndicator) {
        excessIndicator = document.createElement('span');
        excessIndicator.setAttribute('data-excess-indicator', '');
        excessIndicator.className = 'text-sm font-bold text-orange-700 ml-1';
        counterContainer.appendChild(excessIndicator);
      }
      excessIndicator.textContent = `+${excess}`;

      // Auto-update main quantity to match (prevent infinite loop with flag)
      if (!isAutoUpdatingQuantity) {
        isAutoUpdatingQuantity = true;
        updateQuantity(totalSelected);
        isAutoUpdatingQuantity = false;
      }

    } else if (totalSelected < targetQuantity && totalSelected > 0) {
      // Under target - red
      counterContainer.classList.add('border-red-500', 'bg-red-50');
      textElements.forEach(el => el.classList.add('text-red-900'));

      // Remove excess indicator if exists
      const excessIndicator = counterContainer.querySelector('[data-excess-indicator]');
      if (excessIndicator) excessIndicator.remove();

    } else {
      // Default state (0 selected) - blue
      counterContainer.classList.add('border-blue-300', 'bg-white');
      textElements.forEach(el => el.classList.add('text-blue-900'));

      // Remove excess indicator if exists
      const excessIndicator = counterContainer.querySelector('[data-excess-indicator]');
      if (excessIndicator) excessIndicator.remove();
    }
  }

  // ============================================
  // QUANTITY SELECTOR
  // ============================================
  function setupQuantitySelector() {
    const quantityInput = document.querySelector('[data-quantity-input]');
    const decreaseBtn = document.querySelector('[data-quantity-decrease]');
    const increaseBtn = document.querySelector('[data-quantity-increase]');

    if (quantityInput) {
      quantityInput.addEventListener('change', function() {
        updateQuantity(Math.max(1, parseInt(this.value) || 1));
      });
    }

    if (decreaseBtn) {
      decreaseBtn.addEventListener('click', () => {
        updateQuantity(Math.max(1, state.selectedQuantity - 1));
      });
    }

    if (increaseBtn) {
      increaseBtn.addEventListener('click', () => {
        updateQuantity(state.selectedQuantity + 1);
      });
    }
  }

  // ============================================
  // QUICK SELECT BUTTONS
  // ============================================
  function setupQuickSelectButtons() {
    const quickSelectButtons = document.querySelectorAll('[data-quick-select]');

    quickSelectButtons.forEach(button => {
      button.addEventListener('click', function() {
        const quantity = parseInt(this.getAttribute('data-quantity')) || 1;
        updateQuantity(quantity);

        // Visual feedback - highlight selected button
        quickSelectButtons.forEach(btn => btn.classList.remove('border-primary', 'bg-primary/10'));
        this.classList.add('border-primary', 'bg-primary/10');
      });
    });
  }

  function updateQuantity(newQuantity) {
    state.selectedQuantity = newQuantity;

    const quantityInput = document.querySelector('[data-quantity-input]');
    if (quantityInput) quantityInput.value = newQuantity;

    const quantityDisplay = document.querySelector('[data-quantity-display]');
    if (quantityDisplay) quantityDisplay.textContent = newQuantity;

    updateDecorationAvailability();
    updatePricing();
    updateBeanieCounter();
  }

  // ============================================
  // DECORATION SELECTION (NO PRICING IMPACT)
  // ============================================
  function setupDecorationSelection() {
    const decorationCards = document.querySelectorAll('[data-patch-type]');
    
    decorationCards.forEach(card => {
      card.addEventListener('click', function() {
        const type = this.dataset.patchType;
        const minQty = parseInt(this.dataset.minQuantity);
        
        if (state.selectedQuantity >= minQty) {
          state.decorationType = type;
          updateDecorationUI();
          showDecorationConfig(type);
        }
      });
    });

    // Size selection
    const sizeCards = document.querySelectorAll('[data-decoration-size]');
    sizeCards.forEach(card => {
      card.addEventListener('click', function() {
        state.decorationSize = this.dataset.decorationSize;
        document.querySelectorAll('[data-decoration-size]').forEach(c => {
          c.classList.remove('ring-2', 'ring-purple-500', 'border-purple-500');
        });
        this.classList.add('ring-2', 'ring-purple-500', 'border-purple-500');
        updateStatusIndicators();
      });
    });

    // Location selection (including folding-tag)
    const locationCards = document.querySelectorAll('[data-decoration-location]');
    locationCards.forEach(card => {
      card.addEventListener('click', function() {
        state.decorationLocation = this.dataset.decorationLocation;
        document.querySelectorAll('[data-decoration-location]').forEach(c => {
          c.classList.remove('ring-2', 'ring-purple-500', 'border-purple-500');
        });
        this.classList.add('ring-2', 'ring-purple-500', 'border-purple-500');
        updateStatusIndicators();
      });
    });

    // Material selection for leather
    const leatherMaterialCards = document.querySelectorAll('[data-leather-material]');
    leatherMaterialCards.forEach(card => {
      card.addEventListener('click', function() {
        state.decorationMaterial = this.dataset.leatherMaterial;
        state.decorationMaterialTitle = this.dataset.materialTitle;
        document.querySelectorAll('[data-leather-material]').forEach(c => {
          c.classList.remove('ring-2', 'ring-purple-500', 'border-purple-500');
        });
        this.classList.add('ring-2', 'ring-purple-500', 'border-purple-500');
        updateStatusIndicators();
      });
    });

    // Material selection for fabric
    const fabricMaterialCards = document.querySelectorAll('[data-fabric-material]');
    fabricMaterialCards.forEach(card => {
      card.addEventListener('click', function() {
        state.decorationMaterial = this.dataset.fabricMaterial;
        state.decorationMaterialTitle = this.dataset.materialTitle;
        document.querySelectorAll('[data-fabric-material]').forEach(c => {
          c.classList.remove('ring-2', 'ring-purple-500', 'border-purple-500');
        });
        this.classList.add('ring-2', 'ring-purple-500', 'border-purple-500');
        updateStatusIndicators();
      });
    });
  }

  function updateDecorationAvailability() {
    const decorationCards = document.querySelectorAll('[data-patch-type]');
    
    decorationCards.forEach(card => {
      const type = card.dataset.patchType;
      const minQty = parseInt(card.dataset.minQuantity);
      const isAvailable = state.selectedQuantity >= minQty;
      
      card.classList.toggle('opacity-60', !isAvailable);
      card.classList.toggle('cursor-not-allowed', !isAvailable);
      
      const badge = document.querySelector(`[data-patch-badge="${type}"]`);
      if (badge && minQty > 1) {
        if (isAvailable) {
          badge.className = 'absolute top-2 right-2 z-10 px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700 border border-green-300';
          badge.textContent = '✓ Available';
        } else {
          badge.className = 'absolute top-2 right-2 z-10 px-2 py-1 text-xs font-medium rounded-full bg-orange-100 text-orange-700 border border-orange-300';
          badge.textContent = `${minQty}+ required`;
        }
      }
      
      if (!isAvailable && state.decorationType === type) {
        state.decorationType = '';
        updateDecorationUI();
      }
    });
  }

  function updateDecorationUI() {
    document.querySelectorAll('[data-patch-type]').forEach(card => {
      const isSelected = card.dataset.patchType === state.decorationType;
      card.classList.toggle('ring-2', isSelected);
      card.classList.toggle('ring-primary', isSelected);
      card.classList.toggle('border-primary', isSelected);
      card.classList.toggle('bg-primary/5', isSelected);
    });
  }

  function showDecorationConfig(type) {
    const configSection = document.querySelector('[data-patch-config]');
    if (configSection) {
      configSection.style.display = type ? 'block' : 'none';
    }

    // Show/hide shape section - hide for embroidery
    const shapesSection = document.querySelector('[data-decoration-sizes]');
    if (shapesSection) {
      shapesSection.style.display = (type === 'embroidery') ? 'none' : 'block';
    }

    // Show/hide material sections based on decoration type
    const leatherMaterialsSection = document.querySelector('[data-leather-materials]');
    const fabricMaterialsSection = document.querySelector('[data-fabric-materials]');

    if (leatherMaterialsSection) {
      leatherMaterialsSection.style.display = (type === 'leather') ? 'block' : 'none';
    }
    if (fabricMaterialsSection) {
      fabricMaterialsSection.style.display = (type === 'fabric') ? 'block' : 'none';
    }

    // Reset material selection when decoration type changes
    if (type !== 'leather' && type !== 'fabric') {
      state.decorationMaterial = '';
      state.decorationMaterialTitle = '';
    }

    // Reset shape selection for embroidery
    if (type === 'embroidery') {
      state.decorationSize = '';
    }
  }

  // ============================================
  // BEANIE SELECTION
  // ============================================
  function setupBeanieSelection() {
    const beanieVariants = document.querySelectorAll('[data-beanie-variant]');
    
    beanieVariants.forEach(variant => {
      const decreaseBtn = variant.querySelector('[data-decrease]');
      const increaseBtn = variant.querySelector('[data-increase]');
      const quantityInput = variant.querySelector('[data-variant-input]');
      
      if (decreaseBtn && increaseBtn && quantityInput) {
        decreaseBtn.addEventListener('click', () => {
          const currentVal = parseInt(quantityInput.value) || 0;
          quantityInput.value = Math.max(0, currentVal - 1);
          updateBeanieSelection();
        });
        
        increaseBtn.addEventListener('click', () => {
          const currentVal = parseInt(quantityInput.value) || 0;
          quantityInput.value = currentVal + 1;
          updateBeanieSelection();
        });
        
        quantityInput.addEventListener('input', () => {
          const val = Math.max(0, parseInt(quantityInput.value) || 0);
          quantityInput.value = val;
          updateBeanieSelection();
        });
      }
    });
  }

  function updateBeanieSelection() {
    state.beanieSelections = [];

    const beanieVariants = document.querySelectorAll('[data-beanie-variant]');
    beanieVariants.forEach(variant => {
      const input = variant.querySelector('[data-variant-input]');
      const quantity = parseInt(input?.value) || 0;

      if (quantity > 0) {
        variant.classList.add('ring-2', 'ring-blue-500', 'bg-blue-50');

        state.beanieSelections.push({
          variantId: variant.dataset.variantId,
          variantTitle: variant.dataset.variantTitle,
          variantPrice: parseInt(variant.dataset.variantPrice),
          beanieType: variant.dataset.beanieType,
          quantity: quantity
        });
      } else {
        variant.classList.remove('ring-2', 'ring-blue-500', 'bg-blue-50');
      }
    });

    state.isConfigComplete = state.beanieSelections.length > 0;
    updatePricing();
    updateStatusIndicators();
    updateBeanieCounter();
  }

  // ============================================
  // DISCOUNT CALCULATION
  // ============================================
  function getDiscountForQuantity(quantity) {
    const priceContainer = document.querySelector('[data-base-price]');
    if (!priceContainer) return 0;

    // Get discount amounts from data attributes
    const tier1 = parseFloat(priceContainer.getAttribute('data-tier-1-amount')) || 0;
    const tier5 = parseFloat(priceContainer.getAttribute('data-tier-5-amount')) || 0;
    const tier10 = parseFloat(priceContainer.getAttribute('data-tier-10-amount')) || 0;
    const tier25 = parseFloat(priceContainer.getAttribute('data-tier-25-amount')) || 0;
    const tier50 = parseFloat(priceContainer.getAttribute('data-tier-50-amount')) || 0;
    const tier100 = parseFloat(priceContainer.getAttribute('data-tier-100-amount')) || 0;

    // Determine discount based on quantity (highest tier wins)
    if (quantity >= 100) return tier100;
    if (quantity >= 50) return tier50;
    if (quantity >= 25) return tier25;
    if (quantity >= 10) return tier10;
    if (quantity >= 5) return tier5;
    return tier1;
  }

  // ============================================
  // SIMPLIFIED PRICING (BASE PRICE × QTY ONLY)
  // ============================================
  function updatePricing() {
    // Get base price from data attribute
    const priceContainer = document.querySelector('[data-base-price]');
    const basePrice = priceContainer ? parseFloat(priceContainer.getAttribute('data-base-price')) / 100 : 0;

    // Calculate total from selected beanie variants
    let totalPrice = 0;
    let totalQuantity = 0;
    let averagePrice = basePrice; // Start with base price

    state.beanieSelections.forEach(selection => {
      totalPrice += (selection.variantPrice / 100) * selection.quantity;
      totalQuantity += selection.quantity;
    });

    // Determine which quantity to use for discount calculation
    const discountQuantity = totalQuantity > 0 ? totalQuantity : state.selectedQuantity;

    // Get applicable discount
    const discountAmount = getDiscountForQuantity(discountQuantity);
    const discountedPrice = Math.max(0, basePrice - discountAmount);

    if (totalQuantity > 0) {
      averagePrice = totalPrice / totalQuantity;
      // Apply discount to the average price
      averagePrice = Math.max(0, averagePrice - discountAmount);
    } else {
      // If no beanies selected, use discounted base price times selected quantity
      averagePrice = discountedPrice;
      totalPrice = discountedPrice * state.selectedQuantity;
      totalQuantity = state.selectedQuantity;
    }

    // Add rush production cost if selected
    if (state.rushProduction && state.rushProductInfo) {
      const rushCost = (state.rushProductInfo.variantPrice / 100) * totalQuantity;
      totalPrice += rushCost;
    }

    // Update UI
    const pricePerBeanieEl = document.querySelector('[data-price-per-beanie]');
    const priceDescriptionEl = document.querySelector('[data-price-description]');
    const totalPriceEl = document.querySelector('[data-total-price]');
    const startOrderPriceEl = document.querySelector('[data-start-order-price]');

    if (pricePerBeanieEl) {
      pricePerBeanieEl.textContent = `$${averagePrice.toFixed(2)}`;
    }

    // Update price description to show discount status
    if (priceDescriptionEl) {
      if (discountAmount > 0) {
        priceDescriptionEl.innerHTML = `per beanie <span class="text-green-600 font-medium">(save $${discountAmount.toFixed(2)} each)</span>`;
      } else {
        priceDescriptionEl.textContent = 'per beanie';
      }
    }

    if (totalPriceEl) {
      totalPriceEl.textContent = `$${totalPrice.toFixed(2)}`;
    }

    if (startOrderPriceEl) {
      startOrderPriceEl.textContent = `$${totalPrice.toFixed(2)}`;
    }
  }

  // ============================================
  // POWER-UP (RUSH PRODUCTION ONLY)
  // ============================================
  function setupPowerUp() {
    const rushCard = document.querySelector('[data-powerup="rush-production"]');

    if (rushCard) {
      // Capture rush product info from data attributes
      const variantId = rushCard.getAttribute('data-variant-id');
      const variantPrice = rushCard.getAttribute('data-variant-price');
      const variantTitle = rushCard.getAttribute('data-variant-title');

      if (variantId && variantPrice) {
        state.rushProductInfo = {
          variantId: variantId,
          variantPrice: parseInt(variantPrice),
          variantTitle: variantTitle
        };
      }

      rushCard.addEventListener('click', function() {
        state.rushProduction = !state.rushProduction;
        this.classList.toggle('ring-2', state.rushProduction);
        this.classList.toggle('ring-yellow-500', state.rushProduction);
        this.classList.toggle('border-yellow-500', state.rushProduction);
        this.classList.toggle('bg-yellow-50', state.rushProduction);

        // Update pricing to include/exclude rush production
        updatePricing();
      });
    }
  }

  // ============================================
  // LOGO UPLOAD (Keep from original)
  // ============================================
  class LogoUploadManager {
    constructor() {
      this.logoFile = null;
      this.logoBase64 = null;
      this.maxFileSizeMB = 10;
      this.allowedTypes = [
        'image/png', 'image/jpeg', 'image/jpg', 'image/gif', 
        'image/svg+xml', 'application/pdf', 'application/postscript',
        'application/illustrator', 'application/vnd.adobe.illustrator'
      ];
      this.allowedExtensions = ['.png', '.jpg', '.jpeg', '.gif', '.svg', '.pdf', '.eps', '.ai'];
      this.init();
    }

    init() {
      const uploadInput = document.querySelector('[data-logo-upload]');
      const logoLabel = document.querySelector('[data-logo-label]');
      
      if (!uploadInput) return;
      
      uploadInput.addEventListener('change', (e) => {
        this.handleFileSelect(e, logoLabel);
      });
    }

    handleFileSelect(event, labelElement) {
      const file = event.target.files[0];
      if (!file) return;
      
      const fileName = file.name.toLowerCase();
      const fileExtension = fileName.substring(fileName.lastIndexOf('.'));
      
      const validMimeType = this.allowedTypes.includes(file.type);
      const validExtension = this.allowedExtensions.includes(fileExtension);
      
      if (!validMimeType && !validExtension) {
        alert('Invalid file type. Please upload PNG, JPG, GIF, SVG, PDF, EPS, or AI files only.');
        event.target.value = '';
        return;
      }
      
      const fileSizeMB = file.size / (1024 * 1024);
      if (fileSizeMB > this.maxFileSizeMB) {
        showNotification(`File too large. Maximum size is ${this.maxFileSizeMB}MB.`, 'error');
        event.target.value = '';
        return;
      }
      
      this.logoFile = file;
      this.convertToBase64(file, labelElement);
    }

    convertToBase64(file, labelElement) {
      const reader = new FileReader();

      reader.onload = (e) => {
        this.logoBase64 = e.target.result;
        this.updateUI(file, labelElement);
        state.logoFile = file;
        updateStatusIndicators();
        showNotification('Logo uploaded successfully!', 'success');
      };

      reader.onerror = () => {
        showNotification('Error reading file. Please try again.', 'error');
      };

      reader.readAsDataURL(file);
    }

    updateUI(file, labelElement) {
      if (!labelElement) return;
      
      const fileName = file.name.toLowerCase();
      const fileExtension = fileName.substring(fileName.lastIndexOf('.')).toUpperCase();
      const isImage = file.type.startsWith('image/') && !fileName.endsWith('.eps');
      
      if (isImage && this.logoBase64) {
        labelElement.innerHTML = `
          <div class="flex items-center space-x-4">
            <img src="${this.logoBase64}" alt="Logo preview" class="w-16 h-16 object-contain border border-gray-300 rounded">
            <div class="text-left">
              <p class="text-lg font-medium text-green-600">✓ ${file.name}</p>
              <p class="text-sm text-muted-foreground">${(file.size / 1024).toFixed(1)} KB • ${fileExtension}</p>
            </div>
          </div>
        `;
      } else {
        labelElement.innerHTML = `
          <div class="flex items-center space-x-4">
            <div class="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-blue-50 to-purple-50 border-2 border-blue-300 rounded-lg">
              <svg class="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
              </svg>
            </div>
            <div class="text-left">
              <p class="text-lg font-medium text-green-600">✓ ${file.name}</p>
              <p class="text-sm text-muted-foreground">${(file.size / 1024).toFixed(1)} KB • ${fileExtension}</p>
            </div>
          </div>
        `;
      }
    }

    hasLogo() {
      return this.logoFile !== null && this.logoBase64 !== null;
    }

    getLogoData() {
      if (!this.hasLogo()) return null;
      return {
        fileName: this.logoFile.name,
        fileType: this.logoFile.type || 'application/octet-stream',
        fileSize: this.logoFile.size,
        base64Data: this.logoBase64
      };
    }
  }

  let logoManager = null;

  function initLogoManager() {
    logoManager = new LogoUploadManager();
  }

  // ============================================
  // CLOUDINARY UPLOAD (Keep from original)
  // ============================================
  async function uploadLogoToCloudinary(logoData) {
    const CLOUD_NAME = 'dsvcreexw';
    const UPLOAD_PRESET = 'sixhats_custom_logo';
    
    try {
      const formData = new FormData();
      formData.append('file', logoData.base64Data);
      formData.append('upload_preset', UPLOAD_PRESET);
      formData.append('folder', 'beanie-orders');
      
      const timestamp = new Date().toISOString();
      formData.append('context', `original_filename=${logoData.fileName}|upload_date=${timestamp}`);
      formData.append('tags', 'beanie-order,logo,custom-order');
      
      const response = await fetch(
        `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/upload`,
        { method: 'POST', body: formData }
      );
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || 'Upload failed');
      }
      
      const result = await response.json();
      
      return {
        success: true,
        url: result.secure_url,
        publicId: result.public_id,
        resourceType: result.resource_type,
        format: result.format,
        bytes: result.bytes,
        createdAt: result.created_at
      };
      
    } catch (error) {
      console.error('Cloudinary upload error:', error);
      return { success: false, error: error.message };
    }
  }

  // ============================================
  // ORDER BUTTONS & CHECKOUT
  // ============================================
  function setupOrderButtons() {
    const startOrderBtns = document.querySelectorAll('[data-start-order]');
    const finishOrderBtn = document.querySelector('[data-finish-order]');
    
    startOrderBtns.forEach(btn => {
      btn.addEventListener('click', function() {
        const customizationSection = document.querySelector('[data-customization-section]');
        if (customizationSection) {
          customizationSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      });
    });
    
    if (finishOrderBtn) {
      finishOrderBtn.addEventListener('click', handleFinishOrder);
    }
  }

  async function handleFinishOrder() {
    console.log('=== BEANIE CHECKOUT PROCESS ===');

    // Validation
    if (!logoManager?.hasLogo() || !state.decorationType || !state.beanieSelections.length) {
      const missingItems = [];
      if (!logoManager?.hasLogo()) missingItems.push('logo');
      if (!state.decorationType) missingItems.push('decoration type');
      if (!state.beanieSelections.length) missingItems.push('beanie selection');

      showNotification(`Please complete: ${missingItems.join(', ')}`, 'error');
      return;
    }

    // Check decoration configuration
    // Shape is not required for embroidery
    if (state.decorationType !== 'embroidery' && !state.decorationSize) {
      showNotification('Please select decoration shape', 'error');
      return;
    }

    if (!state.decorationLocation) {
      showNotification('Please select decoration location', 'error');
      return;
    }

    // Check material selection for leather and fabric patches
    if ((state.decorationType === 'leather' || state.decorationType === 'fabric') && !state.decorationMaterial) {
      showNotification('Please select a patch material', 'error');
      return;
    }

    // Validate beanie quantity matches selection
    const totalSelected = state.beanieSelections.reduce((sum, beanie) => sum + beanie.quantity, 0);
    if (totalSelected !== state.selectedQuantity) {
      showNotification(`Please select exactly ${state.selectedQuantity} beanies. You currently have ${totalSelected} selected.`, 'error');
      return;
    }

    // Build cart items - BEANIES + OPTIONAL RUSH PRODUCTION
    const cartItems = [];

    // Add beanie variants with decoration properties as NOTES
    const decorationProperties = {
      'Decoration Type': state.decorationType,
      'Decoration Location': state.decorationLocation,
      'Rush Production': state.rushProduction ? 'Yes' : 'No'
    };

    // Add shape if not embroidery
    if (state.decorationType !== 'embroidery' && state.decorationSize) {
      decorationProperties['Decoration Shape'] = state.decorationSize;
    }

    // Add material if applicable (leather or fabric only)
    if (state.decorationMaterial && state.decorationMaterialTitle) {
      decorationProperties['Patch Material'] = state.decorationMaterialTitle;
    }

    state.beanieSelections.forEach(beanie => {
      cartItems.push({
        id: parseInt(beanie.variantId, 10),
        quantity: beanie.quantity,
        properties: decorationProperties
      });
    });

    // Add rush production if selected
    if (state.rushProduction && state.rushProductInfo) {
      const totalBeanieQuantity = state.beanieSelections.reduce((sum, beanie) => sum + beanie.quantity, 0);
      cartItems.push({
        id: parseInt(state.rushProductInfo.variantId, 10),
        quantity: totalBeanieQuantity,
        properties: {
          'Applied to': `${totalBeanieQuantity} beanies`
        }
      });
    }

    console.log('Cart items:', cartItems);

    // Show loading
    const finishBtn = document.querySelector('[data-finish-order]');
    const originalText = finishBtn.textContent;
    finishBtn.disabled = true;
    finishBtn.textContent = 'Processing...';

    try {
      // Add to cart
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: cartItems })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.description || 'Failed to add to cart');
      }

      // Upload logo
      finishBtn.textContent = 'Uploading logo...';
      const logoData = logoManager.getLogoData();
      const uploadResult = await uploadLogoToCloudinary(logoData);
      
      if (!uploadResult.success) {
        throw new Error(`Logo upload failed: ${uploadResult.error}`);
      }

      // Update cart with logo URL and decoration notes
      finishBtn.textContent = 'Finalizing order...';
      
      let cartNote = `Decoration: ${state.decorationType}`;

      // Add shape if not embroidery
      if (state.decorationType !== 'embroidery' && state.decorationSize) {
        cartNote += `\nShape: ${state.decorationSize}`;
      }

      cartNote += `\nLocation: ${state.decorationLocation}`;

      if (state.decorationMaterial && state.decorationMaterialTitle) {
        cartNote += `\nMaterial: ${state.decorationMaterialTitle}`;
      }

      cartNote += `\nRush Production: ${state.rushProduction ? 'Yes' : 'No'}
Logo: ${logoData.fileName}`;

      cartNote = cartNote.trim();

      const cartAttributes = {
        'Logo_Filename': logoData.fileName,
        'Logo_URL': uploadResult.url,
        'Logo_Cloudinary_ID': uploadResult.publicId,
        'Decoration_Type': state.decorationType,
        'Decoration_Size': state.decorationSize,
        'Decoration_Location': state.decorationLocation,
        'Rush_Production': state.rushProduction ? 'Yes' : 'No'
      };

      // Add material if applicable
      if (state.decorationMaterial && state.decorationMaterialTitle) {
        cartAttributes['Decoration_Material'] = state.decorationMaterialTitle;
      }

      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          note: cartNote,
          attributes: cartAttributes
        })
      });

      // Redirect to checkout
      window.location.href = '/checkout';

    } catch (error) {
      console.error('Cart error:', error);
      alert(`Error: ${error.message}`);
      finishBtn.disabled = false;
      finishBtn.textContent = originalText;
    }
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();

// Contact Modal (Keep from original)
(function() {
  'use strict';
  
  function initContactModal() {
    const modal = document.querySelector('[data-contact-modal]');
    const openButtons = document.querySelectorAll('[data-open-contact-modal]');
    const closeButtons = document.querySelectorAll('[data-close-modal]');
    const overlay = document.querySelector('[data-modal-overlay]');

    if (!modal) return;

    function openModal() {
      modal.classList.remove('hidden');
      document.body.style.overflow = 'hidden';
    }

    function closeModal() {
      modal.classList.add('hidden');
      document.body.style.overflow = '';
    }

    openButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        openModal();
      });
    });

    closeButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        closeModal();
      });
    });

    if (overlay) {
      overlay.addEventListener('click', closeModal);
    }

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !modal.classList.contains('hidden')) {
        closeModal();
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initContactModal);
  } else {
    initContactModal();
  }
})();